import type { ComponentProps } from 'react'
import { motion } from 'motion/react'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import type { CustomerOrder, CustomerOrderInvoice } from '../../customerOrdersApi'
import { buildSupportRequestPath } from '../../helpers/supportRequestPath'
import type { CustomerProfile } from '../../types'
import type { CustomerOrderDetailLabels } from './orderDetailShared'
import { CustomerOrderHeroSection } from './CustomerOrderHeroSection'
import { CustomerOrderProductsSection } from './CustomerOrderProductsSection'
import { CustomerOrderStatusSection } from './CustomerOrderStatusSection'
import { CustomerOrderShippingSummarySection } from './CustomerOrderShippingSummarySection'
import { CustomerOrderInvoicesSection } from './CustomerOrderInvoicesSection'
import { CustomerOrderPaymentSidebarSection } from './CustomerOrderPaymentSidebarSection'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type CustomerOrderDetailContentProps = {
  prefersReducedMotion: boolean
  contentTransition: ComponentProps<typeof motion.div>['transition']
  orderDetail: CustomerOrder
  profile: CustomerProfile
  orderInvoices: CustomerOrderInvoice[]
  orderInvoicesLoading: boolean
  orderInvoicesError: string
  invoiceOpenError: string
  invoiceOpeningId: string | null
  locale: ShopLocale
  language: ShopLanguage
  labels: CustomerOrderDetailLabels
  toLocalizedPath: (path: string) => string
  onNavigate: (targetPath: string) => void
  onOpenInvoice: (invoice: CustomerOrderInvoice) => void
  t: TranslateFn
}

export const CustomerOrderDetailContent = ({
  prefersReducedMotion,
  contentTransition,
  orderDetail,
  profile,
  orderInvoices,
  orderInvoicesLoading,
  orderInvoicesError,
  invoiceOpenError,
  invoiceOpeningId,
  locale,
  language,
  labels,
  toLocalizedPath,
  onNavigate,
  onOpenInvoice,
  t,
}: CustomerOrderDetailContentProps) => (
  <motion.div
    key={`order-detail-${orderDetail.id}-${orderDetail.status}`}
    initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
    animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
    exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
    transition={contentTransition}
    className="space-y-6"
  >
    <CustomerOrderHeroSection
      orderDetail={orderDetail}
      language={language}
      locale={locale}
      labels={labels}
      reducedMotion={prefersReducedMotion}
      t={t}
    />

    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
      <div className="space-y-5">
        <CustomerOrderProductsSection
          orderDetail={orderDetail}
          locale={locale}
          labels={labels}
          reducedMotion={prefersReducedMotion}
          t={t}
        />

        <CustomerOrderStatusSection
          orderDetail={orderDetail}
          language={language}
          locale={locale}
          labels={labels}
          reducedMotion={prefersReducedMotion}
          t={t}
        />

        <CustomerOrderShippingSummarySection orderDetail={orderDetail} profile={profile} labels={labels} />

        <CustomerOrderInvoicesSection
          title={t('customer.orderDetail.invoices')}
          loadingLabel={t('customer.orderDetail.invoicesLoading')}
          emptyLabel={t('customer.orderDetail.invoicesEmpty')}
          openInvoiceHintLabel={t('customer.orderDetail.openInvoiceHint')}
          openingInvoiceHintLabel={t('customer.orderDetail.openingInvoiceHint')}
          invoicesLoading={orderInvoicesLoading}
          invoicesError={orderInvoicesError}
          invoiceOpenError={invoiceOpenError}
          orderInvoices={orderInvoices}
          invoiceOpeningId={invoiceOpeningId}
          locale={locale}
          t={t}
          onOpenInvoice={onOpenInvoice}
        />
      </div>

      <aside className="space-y-4 xl:sticky xl:top-6">
        <CustomerOrderPaymentSidebarSection
          orderDetail={orderDetail}
          language={language}
          locale={locale}
          labels={labels}
          reducedMotion={prefersReducedMotion}
          supportRequestPath={buildSupportRequestPath(orderDetail, toLocalizedPath)}
          onNavigate={onNavigate}
          t={t}
        />
      </aside>
    </div>
  </motion.div>
)
